import { createRouter, createWebHistory } from "vue-router";
import { isUnderMaintenance } from "@/utils/maintenance.js";
import { ehErroDeChunk, recarregarParaVersaoNova } from "@/utils/recargaPorChunk.js";
import {
  aoIniciarNavegacao,
  aoTerminarNavegacao,
  aoFalharNavegacao
} from "@/router/navegacao.js";
import { guardaDeRoles } from "@/router/permissoes.js";
// import { useThemeStore } from "@/stores/theme.js";

// Nenhum view é importado estaticamente aqui — toda rota carrega o seu com
// `component: () => import('caminho/literal.vue')`, o que dá um chunk por rota. Use sempre o
// caminho literal: `import()` com template literal (ex.: `import(\`@/views/${x}.vue\`)`) faz o
// bundler expandir o glob inteiro de `@/views/*.vue` e devolve o aviso INEFFECTIVE_DYNAMIC_IMPORT.

import gerencialProcessosRoutes from "@/router/gerencialProcessos.js";
import clientesRoutes from "./clientes.js"
import usuariosColaboradoresRoutes from "./usuariosColaboradores.js"
import usuariosClientesRoutes from "./usuariosClientes.js"
import usuariosAgentesRoutes from "./usuariosAgentes.js"
import cargosRoutes from "./cargos.js"
import invoiceRoutes from "./invoice.js"
import departamentoRoutes from "./departamentos.js"
import centroCustoRoutes from "@/router/centroCusto.js";
import gruposRoutes from "./grupos.js"
import novoColaboradorRoutes from "./novoColaborador.js"
import empresaRoutes from "./empresa.js"
import notificacoesRoutes from "./notificacoes.js"
import depotsRoutes from "./depots.js"
import notasFiscaisPrestadorRoutes from "@/router/notasFiscaisPrestador.js";
import pagamentosAntecipadosRoutes from "./pagamentosAntecipados.js"
import containersRoutes from "@/router/containers.js";
import tiposContainersRoutes from "@/router/tiposContainers.js";
import configuracoesRoutes from "./configuracoes.js"
import recintosAlfandegados from "@/router/recintosAlfandegados.js";
import origensDestinos from "@/router/origensDestinos.js";
import relatoriosRoutes from "./relatorios.js"
import monitoramentosRoutes from "./monitoramentos.js"
import agendaRoutes from "./agenda.js"
import logsRoutes from "./logs.js"
import sessionsRoutes from "./sessions.js"
import companhiasTransporteRoutes from "./companhiasTransporte.js"
import tiposOcorrenciasRoutes from "@/router/tiposOcorrencias.js"
import transportadorasRoutes from "@/router/transportadoras.js";
import ocorrenciasRoutes from './ocorrencias.js'
import relatoriosV2Routes from "@/router/relatoriosV2.js";
import termoUsoRoutes from "@/router/termoUso.js";
import tarefaTipoRoutes from "@/router/tarefaTipo.js";
import analiseResultadoParametrizacao from "@/router/analiseResultadoParametrizacao.js"
import ncm from "@/router/ncms.js";
import perfilEmpresa from "./perfilEmpresa.js";
import statusRoutes from "@/router/statusProcesso.js"
import enriquecerDados from "@/router/enriquecerDados.js"
import documento from "@/router/documento.js"
import feriado from "./feriado.js";
import beneficio from "./beneficio.js";
import titulosLiberadosRoutes from "@/router/liberacaoTitulos.js";
import tipoRiscoRoutes from "@/router/riscoPais.js";
import situacaoRoutes from "@/router/situacaoPais.js";
import certificadoRoutes from "@/router/certificado.js";
import solicitacaoCambios from "@/router/solicitacaoCambio.js";
import tiposArquivo from "@/router/tipoArquivo.js";
import dashboardRoutes from "@/router/dashboards.js";
import equipamento from "./equipamento.js";
import clientesSatya from "./clientesSatya.js";
import analiseResultadoParametrizacaoRoutes from "@/router/analiseResultadoParametrizacao.js"
import analiseResultadoRoutes from "@/router/analiseResultado.js"
import ncmRoutes from "@/router/ncms.js";
import perfilEmpresaRoutes from "./perfilEmpresa.js";



// V2 Routes
import perfilEmpresaV2Routes from "./v2/perfilEmpresa.js";
import dashboardCambio from "./v2/dashboardCambio.js";
import dashboardInvoice from "./v2/dashboardInvoice.js"
import dashboardCliente from "./v2/dashboardCliente.js"
import dashboardSatya from "./v2/dashboardSatya.js";


// 1. Importa todos os arquivos '.js' da pasta router (exceto o index.js)
//
// O `!./*.spec.js` não é zelo: este glob é EAGER, então tudo que casa com ele entra no
// bundle e é executado no navegador. Um arquivo de teste aqui dentro importa `vitest`, que
// não existe fora do vitest — o import estoura antes de qualquer coisa e a aplicação não
// chega a montar: fica parada no splash, sem erro visível para quem usa.
const modules = import.meta.glob(["./*.js", "!./*.spec.js"], { eager: true });
const dynamicRoutes = [];

for (const path in modules) {
  // Desconsiderar index.js para evitar loop infinito
  if (path.includes("index.js")) continue;

  const moduleRoutes = modules[path].default;
  if (Array.isArray(moduleRoutes)) {
    dynamicRoutes.push(...moduleRoutes);
  }
}

const routes = [
  ...dynamicRoutes, // Todas as rotas importadas automaticamente aqui
  {
    path: "/",
    name: "Home",
    component: () => import('@/views/Relatorios/ProcessosEstatisticas/ProcessosEstatisticas.vue'),
    meta: { title: "Início" },
  },
  {
    path: "/form-schema-saver",
    name: "testeFormSchema",
    component: () => import('@/views/FormSchemaSaver/FormSchemaSaver.vue'),
    // Ferramenta interna de edição de schemas: a tela libera todos os campos (IsGranted sempre true)
    meta: { title: "Form Schema Saver", roles: ['admin'] },
  },
  ...gerencialProcessosRoutes,
  ...invoiceRoutes,
  ...relatoriosV2Routes,
  ...clientesRoutes,
  ...empresaRoutes,
  ...novoColaboradorRoutes,
  ...notasFiscaisPrestadorRoutes,
  ...relatoriosRoutes,
  ...usuariosColaboradoresRoutes,
  ...usuariosAgentesRoutes,
  ...cargosRoutes,
  ...departamentoRoutes,
  ...pagamentosAntecipadosRoutes,
  ...gruposRoutes,
  ...monitoramentosRoutes,
  ...depotsRoutes,
  ...agendaRoutes,
  ...termoUsoRoutes,
  ...logsRoutes,
  ...usuariosClientesRoutes,
  ...sessionsRoutes,
  ...centroCustoRoutes,
  ... notificacoesRoutes,
  ...recintosAlfandegados,
  ...origensDestinos,
  ...containersRoutes,
  ...tiposContainersRoutes,
  ... configuracoesRoutes,
  ...companhiasTransporteRoutes,
  ...tiposOcorrenciasRoutes,
  ...transportadorasRoutes,
  ...ocorrenciasRoutes,
  ...tarefaTipoRoutes,
  ...analiseResultadoParametrizacao,
  ...ncm,
  ...perfilEmpresa,
  ...perfilEmpresaV2Routes,
  ...statusRoutes,
  ...enriquecerDados,
  ...documento,
  ...feriado,
  ...beneficio,
  ...titulosLiberadosRoutes,
  ...tipoRiscoRoutes,
  ...situacaoRoutes,
  ...certificadoRoutes,
  ...solicitacaoCambios,
  ...tiposArquivo,
  ...dashboardRoutes,
  ...equipamento,
  ...clientesSatya,
  ...dashboardCambio,
  ...dashboardInvoice,
  ...dashboardCliente,
  ...dashboardSatya,
  ...analiseResultadoParametrizacaoRoutes,
  ...analiseResultadoRoutes,
  ...ncmRoutes,
  ...perfilEmpresaRoutes,
  {
    path: "/about",
    name: "About",
    component: () => import('@/views/AboutView.vue'),
    meta: { title: "Sobre" },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import('@/components/LoginPage.vue'),
    meta: { layout: "empty", title: "Entrar" },
  },
  {
    path: "/logout",
    name: "Logout",
    component: () => import('@/views/LogoutPage.vue'),
    meta: { layout: "empty", title: "Sair" },
  },
  {
    path: "/acesso-negado",
    name: 'forbiddenPage',
    component: () => import('@/views/ForbiddenPage.vue'),
    meta: { layout: "empty", title: "Acesso negado" },
  },
  {
    path: "/manutencao",
    name: 'maintenancePage',
    component: () => import('@/views/MaintenancePage.vue'),
    meta: { layout: "empty" },
  },
  {
    path: "/:pathMatch(.*)",
    name: 'errorPage',
    component: () => import('@/views/ErrorPage.vue'),
    meta: { layout: "empty", title: "Página não encontrada" },
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Prende toda navegação na tela de manutenção enquanto a flag estiver ligada. Vem ANTES do guard
// do routeLoading para não acender o overlay numa navegação que será redirecionada.
router.beforeEach((to) => {
  if (isUnderMaintenance && to.name !== 'maintenancePage') {
    return { name: 'maintenancePage' };
  }
});

// Roles da tela (`meta.roles`, TRI-107): quem não tem cai em /acesso-negado em vez de abrir uma tela
// que falharia em todas as chamadas. Também vem antes do routeLoading, pelo mesmo motivo da manutenção.
// A regra está em @/router/permissoes.js, que o menu lateral também usa.
router.beforeEach(guardaDeRoles);

// Indicador de carregamento e título da aba. O que cada um faz, e por que o afterEach precisa
// distinguir navegação que aconteceu de navegação que foi barrada, está em @/router/navegacao.js
// — que é onde os testes pegam.
router.beforeEach(aoIniciarNavegacao);
router.afterEach(aoTerminarNavegacao);
// Chunk que sumiu no deploy: recarrega uma vez para pegar a versão publicada. Detalhes, e o
// segundo caminho por onde o mesmo erro chega, em @/utils/recargaPorChunk.js.
router.onError((error, to) => {
  aoFalharNavegacao();
  if (ehErroDeChunk(error) && to?.fullPath) {
    recarregarParaVersaoNova(to.fullPath);
  }
});

// router.beforeEach((to) => {
//   const theme = useThemeStore();
//   if (to.meta.darkOnly) {
//     theme.forceDark();
//   } else {
//     theme.clearForceDark();
//   }
// });

export default router;
